import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import LoveMessage from './components/LoveMessage';
import PhotoAlbum from './components/PhotoAlbum';
import FlowerGarden from './components/FlowerGarden';
import HeartAnimation from './components/HeartAnimation';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import ConfettiEffect from './components/ConfettiEffect';
import MusicPlayer from './components/MusicPlayer';
import ParticlesBackground from './components/ParticlesBackground';
import styled, { createGlobalStyle } from 'styled-components';

// Global styles for better responsive design
const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
    
    @media (max-width: 480px) {
      font-size: 12px;
    }
  }

  body {
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    touch-action: manipulation;
  }
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  text-align: center;
  background: linear-gradient(135deg, #fff0f6 0%, #ffe9f3 100%);
  font-family: 'Vazirmatn', sans-serif;
  position: relative;
  overflow: hidden;
`;

const MainContent = styled(motion.main)`
  padding: 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  flex: 1;
  z-index: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3rem; // Add spacing between sections
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
    gap: 1.5rem;
  }
`;

const Section = styled(motion.section)`
  margin-bottom: 2rem;
  border-radius: 20px;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(255, 105, 180, 0.15);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 15px 40px rgba(255, 105, 180, 0.2);
    transform: translateY(-5px);
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 15px;
  }
`;

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100, damping: 12 }
  }
};

function App() {
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setLoading(false);
      // Show confetti after loading
      setTimeout(() => {
        setShowConfetti(true);
      }, 500);
    }, 3000); // Slightly longer to ensure assets are loaded
  }, []);

  return (
    <>
      <GlobalStyle />
      <AppContainer>
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              style={{ width: '100%', height: '100vh' }}
            >
              <LoadingScreen />
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{ width: '100%' }}
            >
              <ParticlesBackground />
              <HeartAnimation />
              {showConfetti && <ConfettiEffect />}
              <Header />
              <MainContent
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <Section variants={itemVariants}>
                  <LoveMessage />
                </Section>
                <Section variants={itemVariants}>
                  <PhotoAlbum />
                </Section>
                <Section variants={itemVariants}>
                  <FlowerGarden />
                </Section>
              </MainContent>
              <Footer />
              <MusicPlayer />
            </motion.div>
          )}
        </AnimatePresence>
      </AppContainer>
    </>
  );
}

export default App;